import { useCallback, useEffect, useRef, useState } from 'react';
import { MicroAppClient } from '@byte-coach/hutbot-mapp-sdk-js';
import { redeemMappToken } from '../api';
import NetworkManager from '../api/NetworkManager';
import { environmentConfig, currentEnvironment } from '../config';
import Cookies from 'js-cookie';
import type { Environment } from '@mapp-byte-connect/config';
import {
  preserveReturnUrl,
  preserveReturnUrlForDeepLink,
  restoreReturnUrlAfterAuth,
} from '../utils/auth-return-url';
import {
  isBytePulseDevModeEnabled,
  isEmbeddedInSuperAppWebView,
} from '../utils/mapp-webview';

const COOKIE_KEYS = {
  mappToken: 'mapp_token',
  accessToken: 'access_token',
  userData: 'user_data',
  environment: 'mapp_environment',
  brandId: 'mapp_brand_id',
} as const;

interface IUser {
  yumId: string;
  firstName: string;
  lastName: string;
  email: string;
  roles: string[];
  permissions?: string[];
}

interface IMAppAuthQueryParams {
  mappToken?: string;
  environment?: Environment;
  brand?: string;
}

const getMAppClient = () => {
  return new MicroAppClient({
    mappId: environmentConfig.APP_MAPP_ID,
    authURL: environmentConfig.APP_MAPP_AUTH_URL,
  });
};

const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';

function isLocalSimulatorAuth(): boolean {
  return isLocalhost && environmentConfig.APP_MAPP_AUTH_URL.includes('localhost:3999');
}

function buildAuthRedirectUrl(): string {
  const authUrl = new URL(environmentConfig.APP_MAPP_AUTH_URL);
  authUrl.searchParams.set('id', environmentConfig.APP_MAPP_ID);
  if (isLocalSimulatorAuth()) {
    authUrl.searchParams.set('returnUrl', `${window.location.pathname}${window.location.search}`);
  }
  return authUrl.toString();
}

function normalizeEnvironment(env: string): Environment {
  if (isLocalhost) return 'local';

  const envLower = env.toLowerCase();
  if (envLower === 'development') return 'dev';
  if (envLower === 'stage') return 'staging';
  if (envLower === 'prod') return 'production';
  if (envLower === 'local' || envLower === 'dev' || envLower === 'staging' || envLower === 'production') {
    return envLower as Environment;
  }
  console.warn(`[auth] Unknown environment '${env}', defaulting to 'local'`);
  return 'local';
}

export function useMAppAuth() {
  const urlSearchParamsRef = useRef(new URLSearchParams(window.location.search));
  const params = Object.fromEntries(urlSearchParamsRef.current.entries()) as IMAppAuthQueryParams;

  NetworkManager.setEnvironment(currentEnvironment);
  if (params.brand) {
    NetworkManager.setBrandId(params.brand);
  }

  const clientRef = useRef(getMAppClient());

  // Once auth fails, halt ALL automatic auth attempts (prevents infinite redirect).
  // Only explicit user actions (initAuth) can resume.
  const authHaltedRef = useRef(false);
  const autoAuthAttemptedRef = useRef(false);

  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState<IUser | undefined>(undefined);
  const [authError, setAuthError] = useState<string | null>(null);
  const [brandId, setBrandId] = useState<string | null>(
    params.brand || Cookies.get(COOKIE_KEYS.brandId) || null
  );
  const [environment, setEnvironment] = useState<Environment>(
    normalizeEnvironment(params.environment || Cookies.get(COOKIE_KEYS.environment) || currentEnvironment)
  );

  useEffect(() => {
    if (user) {
      setIsLoading(false);
    }
  }, [user]);

  const cleanAuthToken = useCallback(() => {
    NetworkManager.clearAuthorizationToken();
    NetworkManager.setBrandId(null);
    Cookies.remove(COOKIE_KEYS.mappToken);
    Cookies.remove(COOKIE_KEYS.accessToken);
    Cookies.remove(COOKIE_KEYS.userData);
    Cookies.remove(COOKIE_KEYS.environment);
    Cookies.remove(COOKIE_KEYS.brandId);
    setBrandId(null);
  }, []);

  const logout = useCallback(async () => {
    cleanAuthToken();
    window.location.href = environmentConfig.APP_MAPP_AUTH_URL;
  }, [cleanAuthToken]);

  // Start MApp auth flow (user-initiated)
  const initAuth = useCallback(() => {
    console.log('[auth] initAuth — redirecting to MApp OAuth');
    authHaltedRef.current = false;
    setAuthError(null);
    preserveReturnUrl();
    if (isLocalSimulatorAuth()) {
      window.location.href = buildAuthRedirectUrl();
      return;
    }
    clientRef.current.authenticate();
  }, []);

  // Retry from error state (clears stale tokens, shows loading)
  const retryAuth = useCallback(() => {
    console.log('[auth] retryAuth — clearing state');
    authHaltedRef.current = false;
    setAuthError(null);
    cleanAuthToken();
    setIsLoading(false);
  }, [cleanAuthToken]);

  const updatePermissions = useCallback(async () => {
    // If auth is halted from a previous failure, skip. Only user action resumes.
    if (authHaltedRef.current) {
      console.log('[auth] updatePermissions skipped — auth halted');
      return;
    }

    try {
      const mappToken =
        urlSearchParamsRef.current.get('mappToken') || Cookies.get(COOKIE_KEYS.mappToken);
      const envParam =
        urlSearchParamsRef.current.get('environment') ||
        Cookies.get(COOKIE_KEYS.environment) ||
        'local';
      const brandId =
        urlSearchParamsRef.current.get('brand') || Cookies.get(COOKIE_KEYS.brandId) || undefined;
      const environment = normalizeEnvironment(envParam);

      if (!mappToken) {
        // SuperApp WebView auto-auth runs after SDK subscribe (see effect below).
        if (
          isEmbeddedInSuperAppWebView() &&
          isBytePulseDevModeEnabled() &&
          !authHaltedRef.current
        ) {
          console.log('[auth] no mappToken — awaiting SuperApp WebView auto-auth');
          setIsLoading(true);
          return;
        }

        console.log('[auth] no mappToken, waiting for user to initiate login');
        setIsLoading(false);
        return;
      }

      console.log('[auth] attempting MApp auth');
      NetworkManager.setEnvironment(environment);
      NetworkManager.setBrandId(brandId ?? null);
      setEnvironment(environment);
      setBrandId(brandId ?? null);

      // Check cached session first
      const accessToken = Cookies.get(COOKIE_KEYS.accessToken);
      const cachedUser = Cookies.get(COOKIE_KEYS.userData);

      if (accessToken && cachedUser) {
        console.log('[auth] restoring cached session');
        NetworkManager.setAuthorizationToken(accessToken);
        setUser(JSON.parse(cachedUser));
        setAuthError(null);
        if (restoreReturnUrlAfterAuth()) return;
        return;
      }

      // Redeem the token via the backend (POST /api/permissions)
      console.log('[auth] redeeming token via backend');
      const permissionResp = await redeemMappToken(mappToken);
      if (permissionResp.status) {
        console.log('[auth] auth succeeded');
        NetworkManager.setAuthorizationToken(permissionResp.accessToken);
        setUser(permissionResp.data);
        setAuthError(null);
        Cookies.set(COOKIE_KEYS.mappToken, mappToken, { expires: 1 });
        Cookies.set(COOKIE_KEYS.accessToken, permissionResp.accessToken, { expires: 1 });
        Cookies.set(COOKIE_KEYS.userData, JSON.stringify(permissionResp.data), { expires: 1 });
        Cookies.set(COOKIE_KEYS.environment, environment, { expires: 1 });
        if (brandId) {
          Cookies.set(COOKIE_KEYS.brandId, brandId, { expires: 1 });
        }
        if (restoreReturnUrlAfterAuth()) return;
      } else {
        throw new Error('Token redemption returned unsuccessful response');
      }
    } catch (error) {
      console.error('[auth] auth failed:', error);
      // HALT all automatic retries. Surface error so user can decide what to do.
      // Do NOT call authenticate() here — that causes infinite redirect loops.
      authHaltedRef.current = true;
      cleanAuthToken();
      setAuthError('Authentication failed. Please try again.');
      setIsLoading(false);
    }
  }, [cleanAuthToken]);

  // Preserve respondent deep links before OAuth strips the URL on return.
  useEffect(() => {
    preserveReturnUrlForDeepLink(urlSearchParamsRef.current.has('mappToken'));
  }, []);

  // Mount: try to restore session or redeem token
  useEffect(() => {
    console.log('[auth] mount — checking for session');
    updatePermissions();
  }, [updatePermissions]);

  // SDK subscription: listen for new tokens
  useEffect((): (() => void) => {
    console.log('[auth] subscribing to MApp SDK');

    const onReceivedRedeemableToken = (data: {
      mappToken: string;
      environment: string;
      brand?: string;
    }) => {
      if (authHaltedRef.current) {
        console.log('[auth] SDK token received but auth halted — ignoring');
        return;
      }

      console.log('[auth] SDK: received redeemable token');
      const { mappToken, environment, brand } = data;
      const normalizedEnv = normalizeEnvironment(environment);
      try {
        Cookies.set(COOKIE_KEYS.mappToken, mappToken, { expires: 1 });
        Cookies.set(COOKIE_KEYS.environment, normalizedEnv, { expires: 1 });
        if (brand) {
          Cookies.set(COOKIE_KEYS.brandId, brand, { expires: 1 });
        }
        setBrandId(brand ?? null);
        setEnvironment(normalizedEnv);
        urlSearchParamsRef.current = new URLSearchParams(window.location.search);
        urlSearchParamsRef.current.set('mappToken', mappToken);
        urlSearchParamsRef.current.set('environment', normalizedEnv);
        if (brand) {
          urlSearchParamsRef.current.set('brand', brand);
        }
      } catch (err) {
        console.error('[auth] failed to save SDK tokens:', err);
      }
      updatePermissions();
    };

    const handleError = (error: unknown) => {
      if (error) {
        console.log('[auth] SDK error:', error);
      }
    };

    const unsub = clientRef.current.subscribe({
      onReceivedRedeemableToken,
      onError: handleError,
    });

    const mappToken =
      urlSearchParamsRef.current.get('mappToken') || Cookies.get(COOKIE_KEYS.mappToken);
    if (
      !mappToken &&
      isEmbeddedInSuperAppWebView() &&
      isBytePulseDevModeEnabled() &&
      !authHaltedRef.current &&
      !autoAuthAttemptedRef.current &&
      !isLocalSimulatorAuth()
    ) {
      autoAuthAttemptedRef.current = true;
      console.log('[auth] auto-starting MApp auth in SuperApp WebView');
      preserveReturnUrl();
      clientRef.current.authenticate();
    }

    return () => {
      console.log('[auth] unsubscribing from MApp SDK');
      if (typeof unsub === 'function') {
        unsub();
      }
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return {
    isLoading,
    user,
    authError,
    logout,
    initAuth,
    retryAuth,
    updatePermissions,
    brandId,
    environment,
  };
}
